/**
 * LA SFIDA DI PIATTAFORMA — invio (I-207, lotto A di `fronti/tma/sfide-fra-amici.md` T2-3).
 *
 * Il motore (qTournaments, kb#296) NON esiste ancora: chi parla e' il BFF `app-qlash-api`, dietro
 * `/v1/challenges` (PR qlash-limulo#169 @phomea, FUSA 2026-09-28 18:21:29Z, `4348dc6`/`f0db426`).
 * Il contratto e' letto nel codice della PR, non ancora provato dal vivo:
 * `/home/dev/.qbridge/scambio/contratto-bff-sfida-pr169.md` — grado [C].
 *
 * Puro per costruzione, come `code.ts` e `registration.ts`: niente rete, niente orologio
 * implicito. `tools/verify-sfide.ts` non lo esegue (e' JS/TS puro senza dipendenze runtime), ma
 * i test vitest si'.
 */
import type {
  LimuloChallengeIdentity,
  LimuloChallengeIdentities,
  LimuloChallengeRole,
  LimuloChallengeState,
  LimuloChallengeWithIdentities,
  LimuloGameMatchMode,
} from '../../types/limulo.ts';
import { CHALLENGE_REASONS, type LimuloChallengeReason, type LimuloProfileReason } from './errors.ts';
import { verdettoDelSync, type EsitoSyncAlTocco, type EsitoSyncProfilo, type ProfiloPerIscrizione } from './registration.ts';

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const asNonEmptyStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;
const asBoolean = (value: unknown): boolean => value === true;
const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

// ─────────────────────────────────────────────────────────────────────────────
// 1. Le rotte — in un punto solo (contratto PR169 §ROTTE)
// ─────────────────────────────────────────────────────────────────────────────

export const CHALLENGE_ROUTES = {
  createOrList: '/v1/challenges',
  read: (challengeId: string) => `/v1/challenges/${encodeURIComponent(challengeId)}`,
  accept: (challengeId: string) => `/v1/challenges/${encodeURIComponent(challengeId)}/accept`,
  decline: (challengeId: string) => `/v1/challenges/${encodeURIComponent(challengeId)}/decline`,
  revoke: (challengeId: string) => `/v1/challenges/${encodeURIComponent(challengeId)}/revoke`,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Il corpo della POST di creazione — la sfida e' NOMINATIVA, mai by-code (dispatch, vincolo)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `POST /v1/challenges`: `gameId`, `gameModeKey`, `target: {sub}` dell'amico, `profileId` del
 * PROPRIO profilo per quel gioco (facoltativo — senza, il motore risponde
 * `sfida.profilo-richiesto`). Nessun `by-code`: L-2287 vuole la sfida nominativa.
 */
export function createChallengeBody(input: {
  gameId: string;
  gameModeKey: string;
  /** Il `sub` QSocials dell'amico sfidato — NON un id Limulo. */
  targetSub: string;
  profileId?: string | null;
}): Record<string, unknown> {
  const gameId = input.gameId.trim();
  const gameModeKey = input.gameModeKey.trim();
  const targetSub = input.targetSub.trim();
  const profilo = (input.profileId ?? '').trim();
  const base: Record<string, unknown> = { gameId, gameModeKey, target: { sub: targetSub } };
  return profilo === '' ? base : { ...base, profileId: profilo };
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Le modalita' che il motore accetta per un 1v1 (contratto PR169 §MOTORE)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Solo le modalita' il cui `teamSize` CONTIENE 1: il motore rifiuta le altre (`partySize 1`,
 * contratto PR169 §MOTORE). Una modalita' senza `teamSize` (il campo e' assente: punti e tempo
 * non sono nel normalizzatore, kb#297) non e' «contiene 1» — e' «non lo sappiamo» — e resta fuori:
 * offrirla rischierebbe un rifiuto del motore su una scelta che sembrava valida.
 */
export function modaliteInviabili(
  modalita: readonly LimuloGameMatchMode[],
): LimuloGameMatchMode[] {
  // ⚠️ `!= null` (non `!== null`): il tipo promette `{min,max} | null`, ma `unwrapItems` non
  // valida a runtime — un campo davvero ASSENTE arriverebbe `undefined`, e `!== null` lo
  // lascerebbe passare fino a `.min`, che lancerebbe (revisione #487, punto 4).
  return modalita.filter((m) => m.teamSize != null && m.teamSize.min <= 1 && m.teamSize.max >= 1);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. La sfida letta dal filo — mai un fatto inventato su uno stato ignoto
// ─────────────────────────────────────────────────────────────────────────────

const CHALLENGE_STATES = ['pending', 'accepted', 'declined', 'revoked', 'expired'] as const satisfies readonly LimuloChallengeState[];
const CHALLENGE_ROLES = ['challenger', 'target', 'acceptor', 'link'] as const satisfies readonly LimuloChallengeRole[];

function identityOf(raw: unknown): LimuloChallengeIdentity | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  return {
    nickname: asStringOrNull(r.nickname),
    name: asString(r.name),
    requiresNickname: asBoolean(r.requiresNickname),
    avatar: asString(r.avatar),
    // Tollera l'ASSENZA (un BFF di prima di qlash-limulo#173): non e' un errore, e' «non lo so» ⇒ `null`.
    // Una stringa vuota non e' un sub: il confronto con `friendUserId` non deve mai poterla soddisfare.
    sub: asNonEmptyStringOrNull(r.sub),
  };
}

/**
 * Una sfida dal filo, o `null` se non ha un `challengeId` o uno `state` che riconosciamo: uno
 * stato ignoto non diventa `pending` per comodita', e una riga che non sappiamo leggere non
 * diventa un fatto — sparisce dall'elenco invece di mentire su cosa sia (stessa regola di
 * `matchOf`/`participantsOf`).
 */
export function challengeOf(raw: unknown): LimuloChallengeWithIdentities | null {
  const c = asRecord(raw);
  const challengeId = asString(c.challengeId);
  if (challengeId === '') return null;
  const state = CHALLENGE_STATES.find((s) => s === c.state);
  if (state === undefined) return null;
  const role = CHALLENGE_ROLES.find((r) => r === c.role) ?? null;
  const idn = asRecord(c.identities);
  return {
    challengeId,
    state,
    gameId: asString(c.gameId),
    gameModeKey: asString(c.gameModeKey),
    resultMode: asString(c.resultMode),
    matchCheckInMinutes: asFiniteNumber(c.matchCheckInMinutes),
    challenger: asString(c.challenger),
    target: asStringOrNull(c.target),
    acceptedBy: asStringOrNull(c.acceptedBy),
    createdAt: asStringOrNull(c.createdAt),
    expiresAt: asStringOrNull(c.expiresAt),
    acceptedAt: asStringOrNull(c.acceptedAt),
    closedAt: asStringOrNull(c.closedAt),
    tournamentId: asStringOrNull(c.tournamentId),
    started: asBoolean(c.started),
    startAbandoned: asBoolean(c.startAbandoned),
    role,
    code: asStringOrNull(c.code),
    gameName: asNonEmptyStringOrNull(typeof c.gameName === 'string' ? c.gameName.trim() : null),
    identities: {
      challenger: identityOf(idn.challenger),
      target: identityOf(idn.target),
      acceptedBy: identityOf(idn.acceptedBy),
    } satisfies LimuloChallengeIdentities,
  };
}

export interface LimuloChallengeList {
  items: LimuloChallengeWithIdentities[];
  total: number | null;
  page: number;
  limit: number;
}

/**
 * `GET /v1/challenges`: la busta ha `data: {items, total, page, limit}` (+ `meta.count`,
 * `meta.pagination`, ridondanti e non letti qui: quello che serve e' gia' dentro `data`,
 * contratto PR169 §ROTTE). Una riga che `challengeOf` non riconosce si scarta, non si inventa.
 */
export function challengeListOf(response: unknown): LimuloChallengeList {
  const data = asRecord(asRecord(response).data);
  const rawItems = Array.isArray(data.items) ? data.items : [];
  const items = rawItems
    .map(challengeOf)
    .filter((c): c is LimuloChallengeWithIdentities => c !== null);
  return {
    items,
    total: asFiniteNumber(data.total),
    page: asFiniteNumber(data.page) ?? 1,
    limit: asFiniteNumber(data.limit) ?? 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Il rifiuto a schermo — sei motivi con un testo loro, gli altri generici col codice in chiaro
// ─────────────────────────────────────────────────────────────────────────────

/**
 * I SEI motivi con una frase dedicata (dispatch sfide-orcr-3-invio, punto 2): gli altri quindici
 * del vocabolario `CHALLENGE_REASONS` restano generici, col codice mostrato in chiaro — non
 * un'omissione, e' la scelta esplicita del dispatch («gli altri motivi hanno un testo generico»).
 *
 * 🔴 **`profilo-rifiutato`, non `profilo-respinto` (decisione del seggio, revisione #487).** Il
 * motore annida `profilo-respinto` DENTRO `profilo-rifiutato` (`sfide.js:197-206`) e il BFF
 * proietta quello ESTERNO: un client che aspetta `profilo-respinto` non lo vedrebbe MAI. Il
 * dispatch nominava `profilo-respinto` perche' cosi' lo leggeva il contratto letto nel codice
 * della PR — la lettura era del motivo giusto nel VOCABOLARIO (i ventuno nomi ci sono entrambi,
 * `CHALLENGE_REASONS`), sbagliata su quale dei due il bordo manda davvero.
 *
 * ⚠️ **`profilo-rifiutato` NON afferma «il profilo e' stato rifiutato» in senso stretto (revisione
 * #487, punto e).** `sfide.js:203-205` annida sotto questo stesso motivo anche `validazione-rotta`
 * e `gioco-muto`: chi lo riceve puo' avere un profilo perfettamente valido, respinto per una
 * ragione che non ha niente a che fare col profilo in se'. La frase resta NEUTRA («il gioco non e'
 * riuscito a verificare il profilo»), e il link dice «Controlla i tuoi profili» — mai «Crealo
 * qui», che a chi un profilo ce l'ha gia' sarebbe falso. Solo `profilo-richiesto` (nessun profilo
 * per costruzione) promette la creazione.
 */
export const CHALLENGE_REASON_COPY: Partial<Record<LimuloChallengeReason, string>> = {
  'sfida.non-amico': 'social.sfida_rifiuto_non_amico',
  'sfida.troppe-in-attesa': 'social.sfida_rifiuto_troppe_in_attesa',
  'sfida.profilo-richiesto': 'social.sfida_rifiuto_profilo_richiesto',
  'sfida.profilo-rifiutato': 'social.sfida_rifiuto_profilo_rifiutato',
  'sfida.gioco-rifiutato': 'social.sfida_rifiuto_gioco_rifiutato',
  'sfida.amicizia-non-verificabile': 'social.sfida_rifiuto_amicizia_non_verificabile',
};

/** I due motivi la cui frase porta anche il link per crearsi/controllare il profilo. */
/**
 * Il testo del link per i due motivi che ne portano uno — DIVERSO per ciascuno (revisione #487,
 * punto e): `profilo-richiesto` non ha un profilo, per costruzione, e il link promette di
 * crearlo; `profilo-rifiutato` puo' capitare a chi un profilo ce l'ha gia' (v. la nota sopra
 * `CHALLENGE_REASON_COPY`), quindi il link dice di controllarli, mai di crearne uno.
 */
export const CHALLENGE_REASON_PROFILE_LINK: Partial<Record<LimuloChallengeReason, string>> = {
  'sfida.profilo-richiesto': 'social.sfida_rifiuto_crea_profilo',
  'sfida.profilo-rifiutato': 'social.sfida_rifiuto_controlla_profili',
};

export type EsitoRifiutoSfida =
  /** Uno dei sei motivi utili: `chiave` e' la chiave i18n gia' pronta. */
  | { kind: 'nota'; motivo: LimuloChallengeReason; chiave: string }
  /** Ogni altro caso: motivo fuori dai sei, motivo ignoto, o nessun motivo dichiarato. `codice`
   *  e' cio' che si mostra in chiaro — il motivo se lo conosciamo, altrimenti quel che resta
   *  dell'errore (il `code` HTTP, o una stringa vuota se non c'e' nemmeno quello). */
  | { kind: 'generico'; codice: string }
  /** I-543: la sfida NON e' partita perche' il profilo di gioco che mancava non si e' potuto creare. `chiave` e' la
   *  frase (con `{{reason}}` se c'e' una `ragione`: il motivo del provider, gia' tradotto; con `{{code}}` se c'e' un
   *  `codice`: kb#388, il codice del vocabolario chiuso o lo status). Non nasce dal motore. */
  | { kind: 'profilo'; chiave: string; ragione?: string; codice?: string };

/**
 * Compone l'esito da mostrare per un rifiuto di `POST /v1/challenges`: il motivo nominato (o
 * `null` se il BFF non l'ha detto) e un codice di ripiego per il caso generico.
 */
export function rifiutoSfidaOf(
  reason: LimuloChallengeReason | null,
  codiceGrezzo: string,
): EsitoRifiutoSfida {
  if (reason !== null) {
    const chiave = CHALLENGE_REASON_COPY[reason];
    if (chiave !== undefined) return { kind: 'nota', motivo: reason, chiave };
    return { kind: 'generico', codice: reason };
  }
  return { kind: 'generico', codice: codiceGrezzo };
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Chi RICEVE — il profilo che si manda, il rifiuto dell'accettazione, la sfida ancora viva
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Il `profileId` da mandare a una sfida di piattaforma, dato l'esito di `profiloPerIscrizione`:
 * la regola di **inviare** (`useInvioSfidaPiattaforma`) e di **accettare** (la barra) e' UNA sola.
 * Un profilo per il gioco ⇒ quello; piu' di uno ⇒ il PRIMO (il selettore e' fuori scopo, in APERTO);
 * nessuno ⇒ `null`, e il motore risponde `sfida.profilo-richiesto`.
 *
 * ⚠️ Il motore vuole il profilo ANCHE da chi accetta: `accettaSfida` chiama `profiloDi(...)` con
 * `dichiarato === undefined` ⇒ `sfida.profilo-richiesto` (`qlash-limulo@4348dc6`,
 * `app-qtournaments/app/core/sfide.js`, `profiloDi`/`accettaSfida`). Accettare senza `profileId`
 * non riesce MAI.
 */
export function profileIdDellaSfida(scelta: ProfiloPerIscrizione): string | null {
  if (scelta.kind === 'uno') return scelta.profileId;
  if (scelta.kind === 'piu') return scelta.opzioni[0]?.profileId ?? null;
  return null;
}

/**
 * Le frasi dell'ACCETTAZIONE. Non si riusa `CHALLENGE_REASON_COPY` a scatola chiusa: cinque delle sue
 * sei frasi parlano di INVIARE («per inviare questa sfida», «la sfida non e' partita»), e a chi sta
 * per accettare sarebbero false. Le due che restano vere (`profilo-rifiutato`, `gioco-rifiutato`)
 * si riusano; le altre hanno la loro frase, e quelle che il motore dice a chi accetta (`scaduta`,
 * `non-in-attesa`, `non-trovata`: la sfida non c'e' piu') la dicono per intero.
 */
export const CHALLENGE_ACCEPT_REASON_COPY: Partial<Record<LimuloChallengeReason, string>> = {
  'sfida.profilo-richiesto': 'social.sfida_accetta_profilo_richiesto',
  'sfida.profilo-rifiutato': 'social.sfida_rifiuto_profilo_rifiutato',
  'sfida.gioco-rifiutato': 'social.sfida_rifiuto_gioco_rifiutato',
  'sfida.scaduta': 'social.sfida_accetta_scaduta',
  'sfida.non-in-attesa': 'social.sfida_accetta_non_in_attesa',
  'sfida.non-trovata': 'social.sfida_accetta_non_in_attesa',
  // Il motore lo emette SOLO su accept (409): chi accetta ha gia' un biglietto di coda abbinato per lo stesso gioco.
  // NON e' in `CHALLENGE_REASONS_CHIUSA`: la sfida resta `pending` fino alla scadenza, e a partita finita si accetta.
  'sfida.abbinato-in-coda': 'social.sfida_accetta_abbinato_in_coda',
};

/** I motivi per cui la sfida NON e' piu' li' da accettare ne' da rifiutare: la riga va tolta, non ritentata. */
export const CHALLENGE_REASONS_CHIUSA: ReadonlySet<LimuloChallengeReason> = new Set([
  'sfida.scaduta',
  'sfida.non-in-attesa',
  'sfida.non-trovata',
]);

/** Come `rifiutoSfidaOf`, ma con le frasi dell'accettazione. Stesso tipo di esito. */
export function rifiutoAccettaSfidaOf(
  reason: LimuloChallengeReason | null,
  codiceGrezzo: string,
): EsitoRifiutoSfida {
  if (reason !== null) {
    const chiave = CHALLENGE_ACCEPT_REASON_COPY[reason];
    if (chiave !== undefined) return { kind: 'nota', motivo: reason, chiave };
    return { kind: 'generico', codice: reason };
  }
  return { kind: 'generico', codice: codiceGrezzo };
}

// ─────────────────────────────────────────────────────────────────────────────
// 6-bis. Il profilo «non collegato» (kb#388, tma-orcr-43)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * I codici ESATTI con cui il game host dice «non ho l'identita'» (`<gioco>.sub.identita-assente|ambigua`, i quattro `lib/sub.js`
 * di qchess, qgammon, burraco, bridge — Limulo origin/staging, letti per ref il 2026-10-09). Niente suffisso libero: un
 * codice che non e' in questo elenco non si mostra mai. ⚠️ OGGI NON ARRIVANO al client: il BFF promuove il 403 del
 * provider a `E_QLASH_HTTP_403` (`router.js:267-281`), quindi a schermo esce lo status. L'elenco serve il giorno in cui il BFF
 * li manda.
 */
export const CODICI_SUB_IDENTITA: ReadonlySet<string> = new Set([
  'chess.sub.identita-assente',
  'chess.sub.identita-ambigua',
  'qgammon.sub.identita-assente',
  'qgammon.sub.identita-ambigua',
  'burraco.sub.identita-assente',
  'burraco.sub.identita-ambigua',
  'bridge.sub.identita-assente',
  'bridge.sub.identita-ambigua',
]);

/** Cio' che si MOSTRA come codice per un 403 del sync: il codice del game host se e' uno degli otto, altrimenti lo status. */
export function codiceIdentitaDaMostrare(status: number, code: string | null): string {
  return code !== null && CODICI_SUB_IDENTITA.has(code) ? code : String(status);
}

/** (b) Il 403 del sync: ritentare non serve, il problema non e' dell'utente. Porta il codice. */
export const CHIAVE_PROFILO_NON_COLLEGATO_IDENTITA = 'social.sfida_profilo_non_collegato_identita';
/** (a) Il server ha detto «non collegato» e l'ultimo sync era caduto: il tocco dopo lo rifa'. */
export const CHIAVE_PROFILO_NON_COLLEGATO_RITENTA = 'social.sfida_profilo_non_collegato_ritenta';
/** (a') Il sync atteso dal tocco e' caduto anche lui: niente sfida, riprova fra qualche minuto. */
export const CHIAVE_COLLEGAMENTO_NON_RISPONDE = 'social.sfida_collegamento_non_risponde';
/** Cio' che si legge mentre il tocco aspetta il sync. */
export const CHIAVE_COLLEGANDO_PROFILO = 'social.sfida_collegando_profilo';

/**
 * L'esito di un tocco che NON parte perche' il sync atteso non e' andato bene (`EsitoSyncAlTocco` diverso da `ok`):
 * la sfida non si manda. Vale per chi invia e per chi accetta, e per il profilo appena CREATO (il sync atteso dalla
 * creazione, che fino a kb#388 si perdeva in silenzio).
 */
export function esitoDaSyncAlTocco(esito: Exclude<EsitoSyncAlTocco, { kind: 'ok' }>): EsitoRifiutoSfida {
  if (esito.kind === 'identita') {
    return {
      kind: 'profilo',
      chiave: CHIAVE_PROFILO_NON_COLLEGATO_IDENTITA,
      codice: codiceIdentitaDaMostrare(esito.status, esito.code),
    };
  }
  return { kind: 'profilo', chiave: CHIAVE_COLLEGAMENTO_NON_RISPONDE };
}

/**
 * Il rifiuto del SERVER `profile.not-linked` (la sfida e' partita e il motore ha detto `sub_missing`), letto insieme
 * all'ultimo esito del sync di QUEL profilo. `null` = nessuna frase speciale (cade sulla frase di sempre): un motivo che
 * non e' `profile.not-linked`; un sync andato bene o mai chiesto (ritentare non e' garantito); un ALTRO 4xx o un 501 del sync
 * (verdetto di configurazione: ritentare non cambia niente, `verdettoDelSync`); nessun profilo noto.
 *  · sync rifiutato 403 ⇒ (b), col codice;
 *  · sync caduto (tetto, rete, 5xx) ⇒ (a): il tocco dopo rifa' il sync.
 */
export function esitoProfiloNonCollegato(
  motivo: LimuloProfileReason | null,
  esitoSync: EsitoSyncProfilo | undefined,
): EsitoRifiutoSfida | null {
  if (motivo !== 'profile.not-linked') return null;
  const verdetto = verdettoDelSync(esitoSync);
  if (verdetto.kind === 'ok') return null;
  if (verdetto.kind === 'identita') {
    return {
      kind: 'profilo',
      chiave: CHIAVE_PROFILO_NON_COLLEGATO_IDENTITA,
      codice: codiceIdentitaDaMostrare(verdetto.status, verdetto.code),
    };
  }
  return { kind: 'profilo', chiave: CHIAVE_PROFILO_NON_COLLEGATO_RITENTA };
}

/**
 * Una sfida in arrivo e ANCORA da rispondere: lo dice il SERVER, e basta (`state === 'pending'`).
 * ⛔ Nessun filtro sull'orologio del telefono: lo stato che arriva e' gia' quello EFFETTIVO (il motore
 * filtra `pending && expiresAt > now`, `filtroDiStato` in `core/sfide.js`), e `azionePersistente.ts`
 * lo vuole cosi' — «un'azione compare e sparisce quando lo dice il server». Un telefono avanti di
 * 20 minuti farebbe sparire una sfida viva. Se e' scaduta fra due giri, il rifiuto `sfida.scaduta`
 * dice una frase umana e la riga se ne va.
 */
export function sfidaDaRispondere(c: LimuloChallengeWithIdentities): boolean {
  return c.state === 'pending';
}

/**
 * **Gli argomenti della lettura delle sfide in ARRIVO, in un posto solo.** La barra (`SorgenteSfidePiattaforma`,
 * che la POLLA) e il tab «Sfide» (che la LEGGE) devono chiedere ESATTAMENTE la stessa cosa: e' la chiave della
 * cache di RTK, e due chiavi diverse sono due GET ogni 60 s per le stesse sfide (condizione c di tma-orcr).
 * ⚠️ Cambiarla qui cambia entrambe; cambiarne una sola e' il difetto che `sfide-tab-costo.test.tsx` misura.
 */
export const SFIDE_IN_ARRIVO = { direction: 'in', state: 'pending', limit: 20 } as const;

/** «Chi sfida» come lo mostra la riga: il nickname, poi il nome, mai un id. `null` se il server non lo dice. */
export function nomeDiChiSfida(c: LimuloChallengeWithIdentities): string | null {
  const id = c.identities.challenger;
  if (id === null) return null;
  const nick = id.nickname?.trim() ?? '';
  if (nick !== '') return nick;
  const nome = id.name.trim();
  return nome === '' ? null : nome;
}

/**
 * Il nome del gioco da mostrare per una sfida: quello che il BFF manda (`gameName`), altrimenti il catalogo
 * (`dalCatalogo`, che ripiega a sua volta sul `gameId`). Il catalogo si passa come funzione perche' e' una
 * lettura di rete (`GET /v1/games`) che chi mostra fa SOLO se serve.
 */
export function nomeDelGiocoDellaSfida(c: LimuloChallengeWithIdentities, dalCatalogo: (gameId: string) => string): string {
  return c.gameName ?? dalCatalogo(c.gameId);
}

export { CHALLENGE_REASONS, type LimuloChallengeReason };
